
import { useState, useRef, useEffect } from 'react';

interface NavbarProps {
  scrolled: boolean;
}

const services = [
  { name: 'Medical SEO', href: '/services/medical-seo' },
  { name: 'Healthcare PPC', href: '/services/healthcare-ppc' },
  { name: 'Medical Website Design', href: '/services/medical-website-design' },
  { name: 'Content Marketing', href: '/services/content-marketing' },
  { name: 'Social Media Marketing', href: '/services/social-media-marketing' },
  { name: 'Reputation Management', href: '/services/reputation-management' },
  { name: 'Marketing Automation', href: '/services/marketing-automation' },
  { name: 'AI Lead Capture', href: '/services/ai-lead-capture' },
  { name: 'Analytics & Reporting', href: '/services/analytics-reporting' },
];

const industries = [
  { name: 'BHRT & Hormone Therapy', href: '/industries/bhrt-hormone-therapy' },
  { name: 'Longevity & Anti-Aging', href: '/industries/longevity-antiaging' },
  { name: 'Functional & Integrative', href: '/industries/functional-integrative' },
  { name: 'Medical Spas', href: '/industries/medical-spas' },
  { name: 'Wellness Centers', href: '/industries/wellness-centers' },
  { name: 'OB/GYN & Menopause', href: '/industries/obgyn-menopause' },
  { name: 'Urology', href: '/industries/urology' },
  { name: 'Cardiology', href: '/industries/cardiology' },
  { name: 'Endocrinology', href: '/industries/endocrinology' },
  { name: 'Preventive Medicine', href: '/industries/preventive-medicine' },
  { name: 'Primary Care', href: '/industries/primary-care-prevention' },
  { name: 'Nurse Practitioners', href: '/industries/nurse-practitioners' },
  { name: 'Physician Assistants', href: '/industries/physician-assistants' },
  { name: 'Clinic Owners', href: '/industries/clinic-owners-directors' },
];

export default function Navbar({ scrolled }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  
  const servicesRef = useRef<HTMLDivElement>(null);
  const industriesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (servicesRef.current && !servicesRef.current.contains(event.target as Node)) {
        setServicesOpen(false);
      }
      if (industriesRef.current && !industriesRef.current.contains(event.target as Node)) {
        setIndustriesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center gap-3 cursor-pointer">
              <div className="w-10 h-10 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center">
                <i className="ri-heart-pulse-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent">MillionDollarMed</span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {/* Services Dropdown */}
              <div ref={servicesRef} className="relative">
                <button
                  onClick={() => { setServicesOpen(!servicesOpen); setIndustriesOpen(false); }}
                  className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-[#7C3AED] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Services
                  <i className={`ri-arrow-down-s-line transition-transform ${servicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {servicesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 animate-fadeIn">
                    {services.map((service) => (
                      <a
                        key={service.href}
                        href={service.href}
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#7C3AED]/5 hover:text-[#7C3AED] transition-colors cursor-pointer"
                      >
                        {service.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Industries Dropdown */}
              <div ref={industriesRef} className="relative">
                <button
                  onClick={() => { setIndustriesOpen(!industriesOpen); setServicesOpen(false); }}
                  className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-[#7C3AED] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Industries
                  <i className={`ri-arrow-down-s-line transition-transform ${industriesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {industriesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 max-h-96 overflow-y-auto animate-fadeIn">
                    {industries.map((industry) => (
                      <a
                        key={industry.href}
                        href={industry.href}
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#7C3AED]/5 hover:text-[#7C3AED] transition-colors cursor-pointer"
                      >
                        {industry.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a href="#automation" className="text-sm font-medium text-gray-700 hover:text-[#7C3AED] transition-colors cursor-pointer whitespace-nowrap">
                Automation
              </a>
              <a href="/about" className="text-sm font-medium text-gray-700 hover:text-[#7C3AED] transition-colors cursor-pointer whitespace-nowrap">
                About
              </a>

              {/* Phone Icon */}
              <a
                href="tel:215-396-3311"
                className="flex items-center gap-2 text-sm font-medium text-[#7C3AED] hover:text-[#6D28D9] transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-[#7C3AED]/10 rounded-full">
                  <i className="ri-phone-fill text-lg"></i>
                </div>
                <span className="hidden xl:inline">215-396-3311</span>
              </a>

              <a
                href="#contact"
                className="bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-6 py-3 rounded-lg hover:from-[#6D28D9] hover:to-[#9333EA] transition-all cursor-pointer whitespace-nowrap font-medium text-sm"
              >
                Book Strategy Call
              </a>
            </div>

            {/* Mobile Menu Button + Phone */}
            <div className="lg:hidden flex items-center gap-3">
              <a
                href="tel:215-396-3311"
                className="w-10 h-10 flex items-center justify-center bg-[#7C3AED]/10 rounded-full text-[#7C3AED] cursor-pointer"
              >
                <i className="ri-phone-fill text-xl"></i>
              </a>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="w-10 h-10 flex items-center justify-center cursor-pointer"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-900`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute top-20 left-0 right-0 bg-white shadow-xl max-h-[calc(100vh-5rem)] overflow-y-auto">
            <div className="px-6 py-4">
              {/* Mobile Services */}
              <div className="border-b border-gray-100 pb-2">
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="flex items-center justify-between w-full py-3 text-base font-medium text-gray-900 cursor-pointer"
                >
                  Services
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileServicesOpen && (
                  <div className="pl-4 pb-2">
                    {services.map((service) => (
                      <a
                        key={service.href}
                        href={service.href}
                        className="block py-2.5 text-sm text-gray-600 hover:text-[#7C3AED] cursor-pointer"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {service.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Industries */}
              <div className="border-b border-gray-100 pb-2">
                <button
                  onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
                  className="flex items-center justify-between w-full py-3 text-base font-medium text-gray-900 cursor-pointer"
                >
                  Industries
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileIndustriesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileIndustriesOpen && (
                  <div className="pl-4 pb-2">
                    {industries.map((industry) => (
                      <a
                        key={industry.href}
                        href={industry.href}
                        className="block py-2.5 text-sm text-gray-600 hover:text-[#7C3AED] cursor-pointer"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {industry.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a 
                href="#automation" 
                className="block py-3 text-base font-medium text-gray-900 hover:text-[#7C3AED] cursor-pointer border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                Automation
              </a>
              <a 
                href="/about" 
                className="block py-3 text-base font-medium text-gray-900 hover:text-[#7C3AED] cursor-pointer border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </a>

              {/* Mobile Phone Link */}
              <a 
                href="tel:215-396-3311" 
                className="flex items-center gap-3 py-3 text-base font-medium text-[#7C3AED] cursor-pointer border-b border-gray-100"
              >
                <i className="ri-phone-fill text-xl"></i>
                Call 215-396-3311
              </a>

              <a
                href="#contact"
                className="block mt-4 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-6 py-3 rounded-lg text-center font-medium cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Book Strategy Call
              </a>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </>
  );
}
